/**
 * TopicSuggestionsPanel - AI-powered topic suggestions.
 *
 * Shows a seed keyword input, "Suggest Topics" button, and a results panel
 * with selectable text and "+" buttons to add keywords to the textarea.
 */

import React, { useState, useCallback } from 'react';
import { Sparkles, RefreshCw, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useSuggestTopics } from '@/hooks/useContentPlannerAPI';
import type { TopicSuggestion, TopicSuggestionRequest } from '@/types/contentPlanner';
import { toast } from 'sonner';

interface TopicSuggestionsPanelProps {
  /** contentType of the currently selected template (suppresses listicle-framed
   * topics on non-listicle templates server-side). */
  templateContentType?: string | null;
  seedKeywords: string[];
  brandBible: Record<string, unknown> | null;
  onAddKeyword: (keyword: string) => void;
  currentKeywordsSet: Set<string>;
  onTopicSelect?: (topic: string) => void;
}

export const TopicSuggestionsPanel: React.FC<TopicSuggestionsPanelProps> = ({
  seedKeywords,
  brandBible,
  onAddKeyword,
  currentKeywordsSet,
  onTopicSelect,
  templateContentType,
}) => {
  const [suggestions, setSuggestions] = useState<TopicSuggestion[]>([]);
  const [customSeed, setCustomSeed] = useState('');
  const suggestMutation = useSuggestTopics();

  const handleSuggest = useCallback(async () => {
    // Use custom seed if provided, otherwise fall back to textarea keywords
    const seeds = customSeed.trim()
      ? [customSeed.trim()]
      : seedKeywords.slice(0, 5);

    if (seeds.length === 0) {
      toast.error('Enter a topic or add keywords first');
      return;
    }

    const request: TopicSuggestionRequest = {
      seed_keywords: seeds,
      count: 8,
      template_content_type: templateContentType ?? undefined,
    };

    if (brandBible) {
      request.brand_bible = {
        niche: (brandBible.niche as string) || undefined,
        services: (brandBible.services_offered as string[]) || undefined,
        target_audience: (brandBible.target_audience as string) || undefined,
      };
    }

    try {
      const result = await suggestMutation.mutateAsync(request);
      if (result.error) {
        toast.error(result.error);
        return;
      }
      setSuggestions(result.suggestions);
      if (result.suggestions.length === 0) {
        toast('No additional topics found');
      }
    } catch {
      toast.error('Failed to generate topic suggestions');
    }
  }, [customSeed, seedKeywords, brandBible, suggestMutation]);

  return (
    <div>
      <div className="flex items-center gap-2">
        <Input
          value={customSeed}
          onChange={(e) => setCustomSeed(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSuggest();
            }
          }}
          placeholder="Enter a topic to explore (or uses your keywords)"
          className="flex-1 h-9 text-sm"
          disabled={suggestMutation.isPending}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={handleSuggest}
          disabled={suggestMutation.isPending || (seedKeywords.length === 0 && !customSeed.trim())}
          className={`${suggestMutation.isPending ? "" : "animate-ai-suggest"} text-teal border-teal/30 hover:bg-teal-light hover:border-teal/50 flex-shrink-0`}
        >
          {suggestMutation.isPending ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin mr-1.5" />
              Researching...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-1.5" />
              Suggest Topics
            </>
          )}
        </Button>
      </div>

      {suggestions.length > 0 && (
        <div className="mt-2 p-3 bg-teal-light border border-teal/30 rounded-lg">
          <p className="text-xs text-teal-dark mb-2 flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            AI-suggested topics{customSeed.trim() ? ` for "${customSeed.trim()}"` : ' based on your keywords'}:
          </p>
          <div className="space-y-0.5">
            {suggestions.map((suggestion, i) => {
              const isSelected = suggestion.keyword === customSeed;
              return (
                <div
                  key={i}
                  className={cn(
                    'flex items-center gap-2 px-2 py-1.5 rounded text-sm',
                    isSelected ? 'bg-teal/20 text-teal-dark' : 'hover:bg-teal/20'
                  )}
                >
                  <button
                    onClick={() => {
                      setCustomSeed(suggestion.keyword);
                      onTopicSelect?.(suggestion.keyword);
                    }}
                    className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center transition-colors bg-teal/30 text-teal-dark hover:bg-teal/50 cursor-pointer"
                    title="Use as topic to explore"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                  <span className="flex-1 min-w-0 select-text cursor-text">
                    {suggestion.keyword}
                  </span>
                  {suggestion.rationale && (
                    <span className="text-xs text-teal select-text cursor-text flex-shrink-0 max-w-[40%] truncate" title={suggestion.rationale}>
                      {suggestion.rationale}
                    </span>
                  )}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {suggestion.exists && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700">
                        exists
                      </Badge>
                    )}
                    {isSelected && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                        selected
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
